import React, { useMemo, useState } from 'react';
import '../../../../../../../Stylesheets/Dashboard/Tabs/Expenses/AddExpenseModal.css';

const CATEGORIES = ['Labor', 'Material', 'Transport', 'Equipment', 'Subcontractor', 'Permit', 'Misc', 'Other'];
const METHODS = ['UPI', 'Cash', 'Bank', 'Card', 'Other'];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED = ['application/pdf', 'image/jpeg', 'image/png'];

const todayISO = () => new Date().toISOString().slice(0, 10);

const AddExpenseModal = ({ onClose, onSubmit, submitting = false }) => {
  const [category, setCategory] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [date, setDate] = useState(todayISO());
  const [paymentMethod, setPaymentMethod] = useState('Other');
  const [billNumber, setBillNumber] = useState('');

  const [billFile, setBillFile] = useState(null);
  const [billError, setBillError] = useState('');

  const [touched, setTouched] = useState({});

  const errors = useMemo(() => {
    const errs = {};
    if (!category) errs.category = 'Select a category.';
    if (amount === '' || Number(amount) <= 0) errs.amount = 'Amount must be greater than 0.';
    if (!date) errs.date = 'Date is required.';
    else if (date > todayISO()) errs.date = 'Date cannot be in the future.';
    if (!paymentMethod) errs.paymentMethod = 'Select a payment method.';
    if (billNumber && billNumber.length > 32) errs.billNumber = 'Bill number ≤ 32 chars.';
    return errs;
  }, [category, amount, date, paymentMethod, billNumber]);

  const isValid = Object.keys(errors).length === 0 && !billError;

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    setBillError('');
    setBillFile(null);
    if (!f) return;
    if (!ALLOWED.includes(f.type)) {
      setBillError('Only PDF, JPG or PNG allowed.');
      return;
    }
    if (f.size > MAX_SIZE) {
      setBillError('Max file size is 10MB.');
      return;
    }
    setBillFile(f);
  };

  const blockInvalidNumberKeys = (e) => {
    if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
  };

  const handleSubmit = () => {
    if (!isValid) {
      setTouched({ category: true, amount: true, date: true, paymentMethod: true, billNumber: true });
      return;
    }
    onSubmit({
      category,
      amount: Number(amount),
      notes: notes.trim(),
      date,                      // YYYY-MM-DD
      paymentMethod,
      billNumber: billNumber.trim() || undefined,
      billFile,                  // <-- send File (null if none)
    });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal animated" role="dialog" aria-modal="true" aria-labelledby="add-expense-title">
        <div className="modal-inner">
          <h2 id="add-expense-title">Add Expense</h2>

          <div className="modal-body">
            <label className="field">
              <span>Category</span>
              <select value={category} onChange={(e) => setCategory(e.target.value)} onBlur={() => setTouched(t => ({ ...t, category: true }))}>
                <option value="">Select Category</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {touched.category && errors.category && <div className="error">{errors.category}</div>}
            </label>

            <label className="field">
              <span>Amount</span>
              <input
                type="number"
                inputMode="decimal"
                placeholder="Enter Amount"
                value={amount}
                onKeyDown={blockInvalidNumberKeys}
                onChange={(e) => setAmount(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, amount: true }))}
                min="0"
                step="0.01"
              />
              {touched.amount && errors.amount && <div className="error">{errors.amount}</div>}
            </label>

            <label className="field">
              <span>Notes <em className="muted">(optional)</em></span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={200} />
              <div className="help">{notes.length}/200</div>
            </label>

            <div className="row-2">
              <label className="field">
                <span>Date</span>
                <input type="date" value={date} max={todayISO()} onChange={(e) => setDate(e.target.value)} onBlur={() => setTouched(t => ({ ...t, date: true }))} />
                {touched.date && errors.date && <div className="error">{errors.date}</div>}
              </label>

              <label className="field">
                <span>Payment Method</span>
                <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} onBlur={() => setTouched(t => ({ ...t, paymentMethod: true }))}>
                  {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
                {touched.paymentMethod && errors.paymentMethod && <div className="error">{errors.paymentMethod}</div>}
              </label>
            </div>

            <label className="field">
              <span>Bill Number <em className="muted">(optional)</em></span>
              <input type="text" placeholder="e.g. INV-2025/0042" value={billNumber} onChange={(e) => setBillNumber(e.target.value)} maxLength={32} />
              {touched.billNumber && errors.billNumber && <div className="error">{errors.billNumber}</div>}
            </label>

            <label className="field">
              <span>Upload Bill <em className="muted">(PDF/JPG/PNG, max 10MB)</em></span>
              <input type="file" accept=".pdf,image/jpeg,image/png" onChange={onPickFile} />
              {billFile && <div className="help">Selected: {billFile.name}</div>}
              {billError && <div className="error">{billError}</div>}
            </label>
          </div>

          <div className="modal-actions">
            <button onClick={handleSubmit} disabled={!isValid || submitting}>{submitting ? 'Adding…' : 'Add Expense'}</button>
            <button onClick={onClose} className="cancel" disabled={submitting}>Cancel</button>
          </div>
        </div>
      </div>
    </div>

  );
};

export default AddExpenseModal;
